import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "./AuthContext";

type Board = {
  id: string;
  name: string;
  cover_image_url: string | null;
  created_at: string;
};

type BoardContextType = {
  boards: Board[];
  loading: boolean;
  error: Error | null;
  refreshBoards: () => Promise<void>;
  updateBoard: (boardId: string, updates: Partial<Board>) => void;
  latestEntry: any | null;
  onNewEntry: (entry: any) => void;
};

const BoardContext = createContext<BoardContextType | undefined>(undefined);

export function BoardProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true); // Start loading by default
  const [error, setError] = useState<Error | null>(null);
  const [latestEntry, setLatestEntry] = useState<any | null>(null);

  const onNewEntry = useCallback((entry: any) => {
    setLatestEntry(entry);
  }, []);

  const updateBoard = useCallback((boardId: string, updates: Partial<Board>) => {
    setBoards(prev => prev.map(board =>
      board.id === boardId ? { ...board, ...updates } : board
    ));
  }, []);

  const fetchBoards = useCallback(async () => {
    // Wait for auth to initialize before making decisions
    if (authLoading) return;

    if (!user) {
      setBoards([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('boards')
        .select(`
          id,
          name,
          cover_image_url,
          created_at,
          board_members!inner(user_id)
        `)
        .eq('board_members.user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setBoards(data || []);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching boards:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  // Initial fetch when user changes or auth finishes loading
  useEffect(() => {
    if (!authLoading) {
      fetchBoards();
    }
  }, [fetchBoards, authLoading]);

  return (
    <BoardContext.Provider value={{ boards, loading, error, refreshBoards: fetchBoards, updateBoard, latestEntry, onNewEntry }}>
      {children}
    </BoardContext.Provider>
  );
}

export function useBoards() {
  const context = useContext(BoardContext);
  if (context === undefined) {
    throw new Error("useBoards must be used within a BoardProvider");
  }
  return context;
}
